import React, { useEffect, useState } from 'react';
import { useFacebookTokenContext } from 'common/context/FacebookTokenContext';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  makeStyles,
  Tooltip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DepartmentAPI, { expiryFacebookAccounts, UpdateFbBannerRequest } from 'components/setting/api/department-api';

type Platform = 'facebook' | 'instagram' | 'whatsapp' | 'linkedin';

interface TokenExpiredBannerProps {
  platform: Platform;
  onReconnect?: (locationId: string, facebookAccountId: number) => void;
  showReconnectButton?: boolean;
  showCloseButton?: boolean;
}

const useStyles = makeStyles((theme) => ({
  banner: {
    position: 'relative',
    top: 48,
    zIndex: theme.zIndex.appBar ,
    backgroundColor: '#fff8e1',
    borderBottom: '1px solid #ffecb3',
    padding: theme.spacing(1, 3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center', 
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  textContainer: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    lineHeight: 1.5,
  },
  locationName: {
    fontWeight: 600,
    color: '#d84315', 
    margin: theme.spacing(0, 0.5),
  },
  reconnectButton: {
    fontWeight: 500,
    textDecoration: 'underline',
    textTransform: 'none',
    minWidth: 'auto',
    padding: 0,
    color: theme.palette.primary.main,
    marginLeft: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  spinner: {
    verticalAlign: 'middle',
    marginLeft: theme.spacing(0.5),
  },
  closeButtonWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%', 
  },
  closeButton: {
    padding: 6,
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: 'transparent',
      color: theme.palette.text.primary,
    },
  },
}));

const TokenExpiredBanner: React.FC<TokenExpiredBannerProps> = ({
  platform,
  onReconnect,
  showReconnectButton = true,
  showCloseButton = true,
}) => {
  const classes = useStyles();
  const [visible, setVisible] = useState(false);
  const [expiryStatus, setExpiryStatus] = useState<expiryFacebookAccounts[]>([]);
  const [loadingMap, setLoadingMap] = useState<{ [key: string]: boolean }>({});
  const [dontShowAgainLoading, setDontShowAgainLoading] = useState(false); // New state for "Don't Show Again" loader

  const { tokenVersion } = useFacebookTokenContext();
  useEffect(() => {
    getTokenStatus();
  }, [platform, tokenVersion]);

  const getTokenStatus = async () => {
    try {
      let result: expiryFacebookAccounts[] = [];
      if (platform === 'facebook') {
        result = await DepartmentAPI.getExpiryFacebookAccounts();
      }
      setExpiryStatus(result);
      setVisible(result.length > 0);
    } catch (error) {
      console.error(`Failed to fetch ${platform} token status`, error);
    }
  };

  const handleReconnect = async (locationId: string, facebookAccountId: number) => {
    setLoadingMap((prev) => ({ ...prev, [locationId]: true }));
    try {
      onReconnect?.(locationId, facebookAccountId);
      await getTokenStatus();
    } finally {
      setLoadingMap((prev) => ({ ...prev, [locationId]: false }));
    }
  };

const handleDontShowAgain = async () => {
    setDontShowAgainLoading(true); 
    try {
      const result: UpdateFbBannerRequest = {
        fbAccountIds: expiryStatus.map((acc) => acc.facebookAccountId),
      };
      if (platform === 'facebook') {
        await DepartmentAPI.UpdateFacebookAccountBanner(result);
      }
      setVisible(false);
    } catch (error) {
      console.error(`Failed to update Facebook account banner`, error);
    } finally {
      setDontShowAgainLoading(false);
    }
  };

  if (!visible) return null;

  const capitalizedPlatform = platform.charAt(0).toUpperCase() + platform.slice(1);

  return (
    <Box className={classes.banner}>
      <Box className={classes.textContainer}>
        <Typography variant="body1" component="div">
          <strong>{capitalizedPlatform} token expired</strong> for the following location
          {expiryStatus.length > 1 ? 's' : ''}:{' '}
          {expiryStatus.map((loc, index) => (
            <React.Fragment key={loc.locationId}>
              <span className={classes.locationName}>{loc.locationName}</span>
              {showReconnectButton && (
                <>
                  <Button
                    className={classes.reconnectButton}
                    onClick={() => handleReconnect(loc.locationId, loc.facebookAccountId)}
                    disabled={!!loadingMap[loc.locationId]}
                  >
                    Reconnect
                  </Button>
                  {loadingMap[loc.locationId] && (
                    <CircularProgress size={14} className={classes.spinner} />
                  )}
                </>
              )}
              {index < expiryStatus.length - 2 ? ', ' : ''}
              {index === expiryStatus.length - 2 ? ' and ' : ''}
              {index < expiryStatus.length - 1 && ' '}
            </React.Fragment>
          ))}
          . Please reconnect your account
          {expiryStatus.length > 1 ? 's' : ''}.
        </Typography>
      </Box>

      {showCloseButton && (
        <Box className={classes.closeButtonWrapper}>
          <>
           <Tooltip title="Don't Show Again">
              <Button
                size="small"
                onClick={handleDontShowAgain}
                className={classes.reconnectButton}
                disabled={dontShowAgainLoading}
              >
                {dontShowAgainLoading ? (
                  <CircularProgress size={14} className={classes.spinner} />
                ) : (
                  "Don't Show Again"
                )}
              </Button>
            </Tooltip>
            <Tooltip title="Dismiss">
              <IconButton
                size="small"
                onClick={() => setVisible(false)}
                className={classes.closeButton}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        </Box>
      )}
    </Box>
  );
};

export default TokenExpiredBanner;
